import { type FormEvent, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, isAdmin } from "../lib/auth";
import { ApiError } from "../lib/api";

const DEMO_USERS: { email: string; label: string; icon: string }[] = [
  { email: "dr.popescu@email.ro", label: "Intră ca rezident (Andrei Popescu)", icon: "fa-stethoscope" },
  { email: "i.marinescu@spital.ro", label: "Intră ca medic (Ioana Marinescu)", icon: "fa-user-doctor" },
  { email: "c.radu@policlinica.ro", label: "Intră ca asistent (Carmen Radu)", icon: "fa-hand-holding-medical" },
  { email: "m.ionescu@mariecurie.ro", label: "Intră ca admin (Maria Ionescu)", icon: "fa-user-shield" },
];

export function Login() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [email, setEmail] = useState("dr.popescu@email.ro");
  const [password, setPassword] = useState("parolamea");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) navigate(isAdmin(user) ? "/admin/dashboard" : "/student/dashboard", { replace: true });
  }, [user, navigate]);

  async function doLogin(em: string, pw: string) {
    setError(null);
    setSubmitting(true);
    try {
      const u = await login(em, pw);
      navigate(isAdmin(u) ? "/admin/dashboard" : "/student/dashboard", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Eroare de conexiune");
      setSubmitting(false);
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    doLogin(email, password);
  }

  return (
    <div className="auth-page">
      <aside className="auth-side">
        <Link to="/" className="brand" style={{ color: "#fff" }}>
          <img src="/logo.svg" alt="" width={44} height={44} />
          <div className="brand-text"><span>Marie Curie</span><small style={{ color: "rgba(255,255,255,.6)" }}>Platformă internă</small></div>
        </Link>
        <div>
          <h2>Continuă să înveți. Continuă să salvezi vieți.</h2>
          <p>Acces nelimitat la cursuri certificate, evaluări obiective și o comunitate de specialiști dedicați.</p>
        </div>
        <div className="auth-quote">
          „Platforma ne-a permis să centralizăm formarea continuă a echipei într-un singur loc. Examenele automate ne economisesc zeci de ore lunar."
          <small>— Dr. Andrei Popescu, Spitalul Clinic de Urgență</small>
        </div>
      </aside>

      <main className="auth-main">
        <div className="auth-form">
          <h1>Bine ai revenit</h1>
          <span className="muted">Introdu datele contului pentru a continua.</span>

          <form onSubmit={onSubmit} style={{ marginTop: 24 }}>
            <div className="form-group">
              <label>Email</label>
              <input className="form-control" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Parolă</label>
              <input className="form-control" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <div className="form-row">
              <label className="form-check"><input type="checkbox" defaultChecked /> Ține-mă conectat</label>
              <Link to="/reset-password" style={{ fontSize: ".88rem" }}>Ai uitat parola?</Link>
            </div>
            {error && (
              <div style={{ padding: "10px 14px", background: "var(--c-accent-soft)", color: "#a8092b", borderRadius: 8, fontSize: ".88rem", marginBottom: 14 }}>
                {error}
              </div>
            )}
            <button className="btn btn-primary btn-lg btn-block" type="submit" disabled={submitting}>
              <span>{submitting ? "Se conectează..." : "Autentificare"}</span>
              {!submitting && <i className="fa-solid fa-arrow-right"></i>}
            </button>
          </form>

          <div style={{ textAlign: "center", marginTop: 24, color: "var(--c-muted)", fontSize: ".92rem" }}>
            Nu ai un cont?{" "}
            <a href="mailto:contact@mariecurie.ro?subject=Solicitare%20cont" style={{ fontWeight: 600 }}>Contactează administratorul</a>
          </div>

          <div className="divider"></div>
          <div style={{ textAlign: "center" }}>
            <small className="muted">Pentru testare rapidă:</small>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10 }}>
              {DEMO_USERS.map((d) => (
                <button
                  key={d.email}
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    setEmail(d.email);
                    setPassword("parolamea");
                    doLogin(d.email, "parolamea");
                  }}
                >
                  <i className={`fa-solid ${d.icon}`}></i> {d.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
